import { MINUTES_PER_DAY } from '../../db/models'
import { endsWord, startsWord } from '../boundaries'
import type { LanguagePack } from '../languages/types'
import type { Candidate, Recognizer } from './types'

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&')

const patterns = new WeakMap<LanguagePack, RegExp>()

/**
 * Zahl, Einheit, optional „für“ davor und Minuten dahinter: „30min“, „1,5 Std.“, „1h30“, „1h 30m“,
 * „für 45 min“, „3t“, „10m“. Die längste Einheit zuerst, damit „minuten“ nicht als „min“ endet.
 */
function patternFor(lang: LanguagePack): RegExp {
  let pattern = patterns.get(lang)
  if (!pattern) {
    const units = Object.keys(lang.durationUnits).sort((a, b) => b.length - a.length).map(escape).join('|')
    const leads = lang.durationLeads.map(escape).join('|')
    pattern = new RegExp(
      `(?:(${leads})\\s+)?(\\d+(?:[.,]\\d+)?)\\s?(${units})(?:(\\d{1,2})(?:min|m)?|\\s(\\d{1,2})\\s?(?:min|m)\\.?)?`,
      'giu',
    )
    patterns.set(lang, pattern)
  }
  pattern.lastIndex = 0
  return pattern
}

/**
 * **Dauer.** Eine Zahl ohne Einheit ist nie eine („Buch 1984 lesen“). Tage sind Aufwand: 1 Tag = 8 Std.
 * und nur ganze (CONTEXT.md, Dauer).
 */
export const recognizeDuration: Recognizer = ({ text, masked, lang }) => {
  const found: Candidate[] = []
  for (const match of masked.matchAll(patternFor(lang))) {
    const start = match.index!
    const end = start + match[0].length
    if (!startsWord(text, start) || !endsWord(text, end)) continue
    // Einbuchstabige Einheiten nur klein: „3D-Drucker“, „Fix 3D printer“ sind keine drei Tage.
    if (match[3]!.length === 1 && match[3] !== match[3]!.toLowerCase()) continue
    // „10m“ schreibt die App selbst für Minuten; mit Leerzeichen („2 m breit“) bleibt es Meter.
    if (match[3] === 'm' && /^\s/u.test(match[0].slice(match[0].indexOf(match[2]!) + match[2]!.length))) continue
    const unit = lang.durationUnits[match[3]!.toLowerCase()]
    const amount = Number(match[2]!.replace(',', '.'))
    if (unit === undefined || !(amount > 0)) continue
    if (unit === 'day') {
      if (!Number.isInteger(amount) || match[4] || match[5]) continue
      found.push({ kind: 'duration', start, end, text: text.slice(start, end), value: { minutes: amount * MINUTES_PER_DAY, mode: 'days' } })
      continue
    }
    const extra = unit === 60 ? Number(match[4] ?? match[5] ?? 0) : 0
    if ((match[4] || match[5]) && unit !== 60) continue
    const minutes = Math.round(amount * unit) + extra
    found.push({ kind: 'duration', start, end, text: text.slice(start, end), value: { minutes, mode: 'duration' } })
  }
  return found
}
